import BackButton from 'components/BackButton';
import RoundedButton from 'components/RoundedButton';
import SnoozeDurationSelectionSection from 'components/subscriptions/SnoozeDurationSelectionSection';
import { useUIStore } from 'hooks/stores/useUIStore';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { trackEvent } from 'utils/analytics/analytics';
import { subscriptionApiClient } from 'utils/apiClient';
import {
  getFormattedSnoozeDuration,
  scrollToSection,
  stringFromDate,
} from 'utils/common';
import { ASSET_PREFIX } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';

export const SNOOZE_DURATION_OPTIONS = {
  durations: [1, 2, 3, 4],
  unit: 'WEEK',
};

const SnoozeSubscriptionSection = observer(
  ({
    showResumeSection,
    subscriptionSnoozed,
    onBack,
    subscriptionId,
    resumeSubscriptionCompletionHandler,
    costPerWeek = undefined,
  }: {
    showResumeSection: boolean;
    subscriptionSnoozed: boolean;
    onBack: (showResume?: boolean) => void;
    subscriptionId: string;
    resumeSubscriptionCompletionHandler: () => void;
    costPerWeek?: string;
  }) => {
    const { displayToast } = useUIStore();
    const [showResume] = useState<boolean>(showResumeSection);
    const [snoozeSubscriptionLoading, setSnoozeSubscriptionLoading] =
      useState<boolean>(false);
    const [snoozed, setSnoozed] = useState<boolean>(subscriptionSnoozed);
    const [resumeDateString, setResumeDateString] = useState<string>();
    const [resumeSubscriptionLoading, setResumeSubscriptionLoading] =
      useState<boolean>(false);
    const validSnoozeDurations = SNOOZE_DURATION_OPTIONS;
    const [snoozeDurationSelected, setSnoozeDurationSelected] =
      useState<number>(1);

    const handleSnoozeSubscriptionPressed = async () => {
      setSnoozeSubscriptionLoading(true);

      const result = await subscriptionApiClient.auth.snoozeSubscription({
        params: {
          subscription_id: subscriptionId,
        },
        body: {
          snoozeDuration: snoozeDurationSelected,
          snoozeUnits: validSnoozeDurations.unit,
        },
      });

      setSnoozeSubscriptionLoading(false);

      if (result.status === HttpStatusCode.OK) {
        setResumeDateString(stringFromDate(result.body.snoozedUntil));
        setSnoozed(true);
        displayToast({
          title: `Your subscription has been snoozed for ${getFormattedSnoozeDuration(
            snoozeDurationSelected,
            validSnoozeDurations.unit
          )}.`,
          timeout: 3500,
        });

        trackEvent('SubscriptionSnoozed', {
          source: 'managesubscription',
          snoozeDuration: snoozeDurationSelected,
          unit: validSnoozeDurations.unit,
        });
      } else {
        console.error(
          `Subscription could not be snoozed. Reason: ${result.body.message}`
        );
      }
    };

    useEffect(() => {
      scrollToSection('#subscriptionTopPage');
    }, []);

    if (snoozed || showResume)
      return (
        <div
          className="headerRow flexCentered"
          style={{
            flexDirection: 'column',
            textAlign: 'center',
            padding: '2em 0',
          }}
        >
          <img
            src={`${ASSET_PREFIX}/images/account/subscriptions/snooze.svg`}
            style={{ height: '30px' }}
          />

          <p style={{ fontSize: '1.4em', fontWeight: '800', margin: '1em 0' }}>
            {!showResume ? 'Subscription snoozed' : 'Resume Subscription'}
          </p>

          {!showResume && (
            <p style={{ fontWeight: 600 }}>
              You've chosen to snooze your subscription for{' '}
              <strong>
                {getFormattedSnoozeDuration(
                  snoozeDurationSelected,
                  validSnoozeDurations.unit
                )}
              </strong>
              .
            </p>
          )}
          {showResume && costPerWeek && (
            <p style={{ fontWeight: 600 }}>
              Click below to resume your subscription now. Once you do, you will
              be charged {costPerWeek} immediately and {costPerWeek} per week
              moving forward.
            </p>
          )}

          {resumeDateString && (
            <p style={{ fontWeight: 600 }}>
              It will renew on or around {resumeDateString}
            </p>
          )}

          {showResume && (
            <RoundedButton
              loading={resumeSubscriptionLoading}
              onClick={async () => {
                setResumeSubscriptionLoading(true);

                const result =
                  await subscriptionApiClient.auth.resumeSubscription({
                    params: {
                      subscription_id: subscriptionId,
                    },
                  });

                setResumeSubscriptionLoading(false);

                if (result.status === HttpStatusCode.OK) {
                  displayToast({
                    title: 'Your subscription has been resumed.',
                    timeout: 3500,
                  });
                  resumeSubscriptionCompletionHandler();
                  onBack(true);
                } else {
                  console.error(
                    `Subscription could not be resumed. Reason: ${result.body.message}`
                  );
                }
              }}
              style={{
                margin: '1em auto',
                width: '100%',
                maxWidth: '300px',
              }}
            >
              Resume Subscription Now
            </RoundedButton>
          )}

          <BackButton onClick={() => onBack(true)} />
        </div>
      );

    return (
      <div
        className="headerRow"
        style={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          borderBottom: 'none',
        }}
      >
        <img
          src={`${ASSET_PREFIX}/images/account/subscriptions/snooze.svg`}
          style={{ height: '30px' }}
        />

        <p style={{ fontSize: '1.4em', fontWeight: '800', margin: '1em 0' }}>
          Why not snooze?
        </p>

        <SnoozeDurationSelectionSection
          durations={validSnoozeDurations?.durations}
          durationUnit={validSnoozeDurations?.unit}
          selectedDuration={snoozeDurationSelected}
          onSelectHandler={setSnoozeDurationSelected}
        />

        <RoundedButton
          loading={snoozeSubscriptionLoading}
          onClick={handleSnoozeSubscriptionPressed}
          style={{ margin: '2em auto 1em', width: '100%', maxWidth: '300px' }}
        >
          Snooze Subscription
        </RoundedButton>

        <BackButton onClick={onBack} />
      </div>
    );
  }
);

export default SnoozeSubscriptionSection;
